import { useCallback, useEffect, useRef, useState } from "react";
import { collabConnectInfo } from "./api";
import type { CollabPeer } from "../../../src/admin/collab-protocol.ts";

export type { CollabPeer };
export { COLLAB_COLORS, colorOf } from "../../../src/admin/collab-protocol.ts";
import { sameCollabView } from "./collab-view";
export { sameCollabView };

/**
 * 协同编辑：连上「代理 + 版本」的房间，告诉别人我在哪页、在填哪格、鼠标在哪，收别人的同样信息。
 * 房间只转发、不存库、不碰版本号（src/admin/collab-room.ts）。
 *
 * 省钱的几条（Cloudflare 按消息与房间醒着的时长计费）：
 * - 鼠标最多每 200ms 发一次，而且**同一页上没有别人就不发**——一个人编辑时一条鼠标消息都没有
 * - 页面 / 在填哪格变了才发，鼠标没动不发
 * - 标签页切到后台就断开，回来再连
 * - 心跳 30 秒一次，房间自动回 pong，不唤醒房间
 */
const CURSOR_MS = 200;
const PING_MS = 30_000;
/** 主动探活（回到前台时）等 pong 的时长 */
const PROBE_MS = 5_000;
/** 握手最长等多久 */
const CONNECT_MS = 10_000;

/**
 * 离线的人最后停的鼠标：页面切到后台（离开）或已经断开的人，鼠标留在最后的位置，画成半透明并写「已离线」。
 * 只认对方的 leave（他真走了）；我自己断线时清空——那不是他离线
 */
export interface CollabGhost { id: string; name: string; a0: string; page: string; version: number | null; cursor: { x: number; y: number }; at: number }
/** 离线的鼠标最多留多久（从他最后一次动鼠标算） */
export const GHOST_MS = 30 * 60_000;

export interface SavedEvent { by: string; revision: number; summary: string; version: number | null; a0: string }
/** 5 分钟没动（没输入、没动鼠标、没滚动、没换页）算空闲 */
const IDLE_MS = 5 * 60_000;
/** 页面在后台多久之后才真断开：短时间切走（看别的标签页、窗口被挡住）人还在，只是标成「离开」；忘在后台过夜的不留在别人的在线列表里 */
const AWAY_DROP_MS = 30 * 60_000;

export function useCollab({ enabled, a0, version, versioned, page, selUid, onSaved }: {
  /** 登录了就连（全部代理共用一个房间，服务端按权限过滤），不限版本化页面 */
  enabled: boolean;
  /** 当前代理：带在每条状态里。鼠标、描格只发给同代理同版本同页的人；「存了」由调用方按代理 + 版本过滤 */
  a0: string | null;
  /** 眼前在看的配置版本；不在版本化页面上为 null */
  version: number | null;
  /** 当前页读不读配置：不读的话鼠标、描格不按版本分 */
  versioned: boolean;
  page: string;
  /** 选中的关卡 uid（别人跳过来时选同一关） */
  selUid: string | null;
  /** 别人存到了第几号：调用方据此把这一版跟上 */
  onSaved: (e: SavedEvent) => void;
}) {
  const [peers, setPeers] = useState<CollabPeer[]>([]);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  /** 每个人最后一次的鼠标（同代理同版本同页才算数）：他切到后台时 cursor 会变成 null，靠这里记住停在哪 */
  const lastCursor = useRef(new Map<string, CollabGhost>());
  /** 已经断开的人留下的鼠标 */
  const [gone, setGone] = useState<CollabGhost[]>([]);
  const track = (p: CollabPeer) => {
    const m = lastCursor.current, prev = m.get(p.id);
    if (p.cursor) m.set(p.id, { id: p.id, name: p.name, a0: p.a0, page: p.page, version: p.version, cursor: p.cursor, at: Date.now() });
    // 鼠标移出内容区、换了页：不留。只有切到后台时保留最后的位置
    else if (!p.away || !prev || prev.a0 !== p.a0 || prev.page !== p.page || prev.version !== p.version) m.delete(p.id);
  };
  /** 我这边断线：别人的状态全部作废，离线的鼠标也不留 */
  const forget = () => { lastCursor.current.clear(); setGone([]); };
  const peersRef = useRef<CollabPeer[]>([]);
  peersRef.current = peers;
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;
  const pageRef = useRef(page);
  pageRef.current = page;
  const versionRef = useRef(version);
  versionRef.current = version;
  const versionedRef = useRef(versioned);
  versionedRef.current = versioned;
  const a0Ref = useRef(a0);
  a0Ref.current = a0;
  const selRef = useRef(selUid);
  selRef.current = selUid;
  const stateRef = useRef<{ field: CollabPeer["field"]; cursor: CollabPeer["cursor"]; scroll: number | null; idleMin: number | null; away: boolean }>({ field: null, cursor: null, scroll: null, idleMin: null, away: typeof document !== "undefined" && document.visibilityState === "hidden" });
  /** 同版本同页上有没有别人：只有这时才发鼠标 */
  const sameView = (p: CollabPeer) => sameCollabView(p, { a0: a0Ref.current ?? "", page: pageRef.current, version: versionRef.current, versioned: versionedRef.current });
  const lastSent = useRef("");
  /*
   * 页面到后台（切了标签页，或者 macOS 上窗口被别的程序整个挡住——Chrome 也算 hidden）不断开，只标「离开」：
   * 以前一 hidden 就断，同事的浏览器被别的窗口一挡就从所有人的顶栏消失，看起来像协同坏了。
   * 后台连着不花钱：心跳由 DO 自动回（setWebSocketAutoResponse），不唤醒房间。后台超过 30 分钟才真断开
   */
  const [dropped, setDropped] = useState(false);
  /** 回到前台时发现连接没了（后台断线、重连还在退避或换票失败）：加一下，连接 effect 立刻重来 */
  const [kick, setKick] = useState(0);
  /** 回到前台时探一下当前连接还活不活（由连接 effect 填） */
  const probeRef = useRef<(() => void) | null>(null);

  /**
   * 发状态。`force`：连接刚建好 / 收到 hello / 前后台切换，必须发；其余是普通更新。
   * 页面在后台时普通更新一律不发（后台跟随别人换页、选关、滚动都会改这些）——每条都会唤醒房间并广播，
   * 后台只该有「我离开了」那一条和自动应答的心跳。回到前台时 force 发一次最新的完整状态
   */
  const send = useCallback((force = false) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    if (stateRef.current.away && !force) return;
    // 同版本同页没别人：鼠标不发（省消息），其余照发（顶栏要知道你在哪、跳过来要用）
    const samePage = peersRef.current.some(sameView);
    const st = stateRef.current;
    const msg = { t: "state", a0: a0Ref.current, page: pageRef.current, version: versionRef.current, selUid: selRef.current, field: st.field, cursor: samePage && !st.away ? st.cursor : null, scroll: st.scroll, idleMin: st.idleMin, away: st.away };
    const s = JSON.stringify(msg);
    if (!force && s === lastSent.current) return;
    lastSent.current = s;
    ws.send(s);
  }, []);

  // 前后台：切到后台标「离开」，30 分钟没回来才断开；回来时取消离开（断了的会重连）
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const h = () => {
      const away = document.visibilityState === "hidden";
      clearTimeout(timer);
      stateRef.current = { ...stateRef.current, away, cursor: away ? null : stateRef.current.cursor };
      if (away) timer = setTimeout(() => setDropped(true), AWAY_DROP_MS);
      else {
        setDropped(false);
        // 后台期间断了、还没连回来：别等退避，马上重连
        if (!wsRef.current) setKick((k) => k + 1);
        // 还「开着」也不一定活着：后台期间换了网络，浏览器可能还没发现。马上探一下，几秒没回音就重连
        else probeRef.current?.();
      }
      send(true);
    };
    if (stateRef.current.away) timer = setTimeout(() => setDropped(true), AWAY_DROP_MS);
    document.addEventListener("visibilitychange", h);
    return () => { document.removeEventListener("visibilitychange", h); clearTimeout(timer); };
  }, [send]);

  // 连接：后台太久断开 / 回来时重连（全部代理一个房间，换代理、换版本都不用重连，发一条状态就行）；断了按 1s 起翻倍、封顶 30s 重试
  useEffect(() => {
    if (!enabled || dropped) return;
    let closed = false, retry = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    /** 当前这条连接的心跳与探活计时器怎么停（每条连接各管各的：旧连接迟到的回调不能停掉新连接的心跳） */
    let stopCurrent: (() => void) | undefined;
    const open = async () => {
      const { token } = await collabConnectInfo();
      if (closed) return;
      // 一时换不到票（网络抖、票过期正在换）：照断线的退避接着试，不能就此放弃——否则要刷新页面才连得回来
      if (!token) { timer = setTimeout(open, Math.min(30_000, 1000 * 2 ** retry++)); return; }
      const url = `${location.origin.replace(/^http/, "ws")}/admin/api/collab/ws?a0=${encodeURIComponent(a0Ref.current ?? "")}`;
      // collab2：跨代理协议（服务端据此进全局房间；老页面还在用 collab，进的是按代理分的旧房间）
      const ws = new WebSocket(url, ["collab2", token]);
      wsRef.current = ws;
      /*
       * 存活：心跳 ping 由 DO 自动回 pong。太久没 pong = 连接已经死了、只是浏览器还没发现（切网、睡眠、代理掐掉），
       * 当场按断线处理并重连——不能等浏览器自己判，那可能要好几分钟，期间顶栏是假在线、别人存了也收不到
       */
      let lastPong = Date.now();
      let ping: ReturnType<typeof setInterval> | undefined;
      let probeTimer: ReturnType<typeof setTimeout> | undefined;
      // 握手也要有期限：后台断网时握手可能一直挂着，既不 open 也不 close，前台探活又只认已连上的连接
      let handshake: ReturnType<typeof setTimeout> | undefined;
      const stop = () => { clearInterval(ping); clearTimeout(probeTimer); clearTimeout(handshake); };
      stopCurrent = stop;
      const dead = () => {
        if (closed || wsRef.current !== ws) return;
        stop();
        wsRef.current = null;
        setConnected(false);
        setPeers([]);
        forget();
        timer = setTimeout(open, Math.min(30_000, 1000 * 2 ** retry++));
        try { ws.close(); } catch { /* 已经坏了 */ }
      };
      handshake = setTimeout(() => { if (ws.readyState !== WebSocket.OPEN) dead(); }, CONNECT_MS);
      probeRef.current = () => {
        if (wsRef.current !== ws) return;
        // 还在握手：回到前台的人不该干等浏览器判失败，直接重来
        // 判死后不走退避，直接重开一轮（kick 让连接 effect 重来，retry 归零）
        if (ws.readyState === WebSocket.CONNECTING) { dead(); setKick((k) => k + 1); return; }
        if (ws.readyState !== WebSocket.OPEN) return;
        const sentAt = Date.now();
        ws.send("ping");
        clearTimeout(probeTimer);
        probeTimer = setTimeout(() => { if (lastPong < sentAt) { dead(); setKick((k) => k + 1); } }, PROBE_MS);
      };
      ws.onopen = () => {
        clearTimeout(handshake);
        retry = 0; lastPong = Date.now(); setConnected(true); lastSent.current = ""; send(true);
        ping = setInterval(() => {
          if (ws.readyState !== WebSocket.OPEN) return;
          if (Date.now() - lastPong > PING_MS * 2 + PROBE_MS) { dead(); return; }
          ws.send("ping");
        }, PING_MS);
      };
      ws.onmessage = (ev) => {
        // 已经换了代理 / 重连过：旧连接上来的消息一律不收
        if (closed || wsRef.current !== ws) return;
        if (ev.data === "pong") { lastPong = Date.now(); return; }
        if (typeof ev.data !== "string") return;
        let m: { t: string; peers?: CollabPeer[]; peer?: CollabPeer; id?: string; by?: string; revision?: number; summary?: string; version?: number | null; a0?: string };
        try { m = JSON.parse(ev.data); } catch { return; }
        if (m.t === "hello" && m.peers) {
          const ps = m.peers;
          lastCursor.current.clear();
          ps.forEach(track);
          setGone((gs) => gs.filter((g) => !ps.some((p) => p.name === g.name)));
          setPeers(ps); send(true);
        }
        else if (m.t === "peer" && m.peer) {
          const p = m.peer;
          track(p);
          // 断开过的人回来了（新连接、新 id）：他留下的离线鼠标收掉
          setGone((gs) => gs.some((g) => g.name === p.name) ? gs.filter((g) => g.name !== p.name) : gs);
          setPeers((xs) => [...xs.filter((x) => x.id !== p.id), p]);
        }
        else if (m.t === "leave" && m.id) {
          const id = m.id, g = lastCursor.current.get(id);
          lastCursor.current.delete(id);
          if (g) setGone((gs) => [...gs.filter((x) => x.id !== id && Date.now() - x.at < GHOST_MS), g]);
          setPeers((xs) => xs.filter((x) => x.id !== id));
        }
        else if (m.t === "saved" && typeof m.revision === "number") onSavedRef.current({ by: m.by ?? "?", revision: m.revision, summary: m.summary ?? "", version: m.version ?? null, a0: m.a0 ?? "" });
      };
      ws.onclose = () => {
        stop();
        // 已经换了代理 / 后台超时断开后又重连了：这是旧连接迟到的 close，不能清掉新连接的在线状态，也不该再排重试
        if (closed || wsRef.current !== ws) return;
        setConnected(false);
        setPeers([]);
        forget();
        if (wsRef.current === ws) wsRef.current = null;
        if (!closed) timer = setTimeout(open, Math.min(30_000, 1000 * 2 ** retry++));
      };
    };
    void open();
    return () => { closed = true; clearTimeout(timer); stopCurrent?.(); wsRef.current?.close(); wsRef.current = null; setPeers([]); forget(); setConnected(false); };
  }, [enabled, dropped, kick, send]);

  // 换代理 / 换页 / 换版本 / 换选中的关：立刻告诉别人
  useEffect(() => { stateRef.current = { ...stateRef.current, field: null, cursor: null }; send(); }, [a0, page, version, send]);
  useEffect(() => { send(); }, [selUid, send]);

  // 滚动：停下来 300ms 再发一次（滚的过程中不发）
  useEffect(() => {
    if (!enabled) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onScroll = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        stateRef.current.scroll = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
        send();
      }, 300);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); clearTimeout(timer); };
  }, [enabled, send]);

  // 空闲：5 分钟没任何动静就告诉别人（头像变灰），之后每分钟更新一次分钟数；一动就恢复
  useEffect(() => {
    if (!enabled) return;
    let last = Date.now();
    const active = () => {
      last = Date.now();
      if (stateRef.current.idleMin != null) { stateRef.current.idleMin = null; send(); }
    };
    const evs = ["mousemove", "keydown", "scroll", "focusin", "pointerdown"] as const;
    for (const e of evs) window.addEventListener(e, active, { passive: true, capture: true });
    const tick = setInterval(() => {
      // 页面在后台：已经标了「离开」，不再逐分钟报空闲——每条状态都会唤醒房间并广播，后台连接只该有自动应答的心跳
      if (stateRef.current.away) return;
      const idle = Date.now() - last;
      const min = idle >= IDLE_MS ? Math.floor(idle / 60_000) : null;
      if (min !== stateRef.current.idleMin) { stateRef.current.idleMin = min; send(); }
    }, 30_000);
    return () => { for (const e of evs) window.removeEventListener(e, active, { capture: true }); clearInterval(tick); };
  }, [enabled, send]);

  // 在填哪格：焦点进出协同范围内的输入控件（主内容区与弹窗）
  useEffect(() => {
    if (!enabled) return;
    const onIn = (e: FocusEvent) => { stateRef.current.field = fieldKeyOf(e.target as Element); send(); };
    const onOut = () => { stateRef.current.field = null; send(); };
    document.addEventListener("focusin", onIn);
    document.addEventListener("focusout", onOut);
    return () => { document.removeEventListener("focusin", onIn); document.removeEventListener("focusout", onOut); };
  }, [enabled, send]);

  // 鼠标：相对主内容区的位置，节流；同页没别人时 send 自己会把鼠标去掉
  useEffect(() => {
    if (!enabled) return;
    let last = 0, pending: ReturnType<typeof setTimeout> | undefined;
    const onMove = (e: MouseEvent) => {
      const root = document.querySelector("[data-collab-main]");
      if (!root || !peersRef.current.some(sameView)) return;
      const r = root.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width, y = (e.clientY - r.top) / r.height;
      stateRef.current.cursor = x >= 0 && x <= 1 && y >= 0 && y <= 1 ? { x, y } : null;
      const now = Date.now();
      clearTimeout(pending);
      if (now - last >= CURSOR_MS) { last = now; send(); } else pending = setTimeout(() => { last = Date.now(); send(); }, CURSOR_MS - (now - last));
    };
    document.addEventListener("mousemove", onMove);
    return () => { document.removeEventListener("mousemove", onMove); clearTimeout(pending); };
  }, [enabled, send]);

  /** 我刚存到了第几号：告诉别人跟上 */
  const announceSaved = useCallback((revision: number, summary: string) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ t: "saved", revision, summary, version: versionRef.current }));
  }, []);

  /** 离线的鼠标：页面在后台的人（还在列表里）+ 已经断开的人 */
  const ghosts = [...peers.filter((p) => p.away).flatMap((p) => lastCursor.current.get(p.id) ?? []), ...gone];

  return { peers, ghosts, connected, announceSaved };
}

/*
 * 「在填哪格」怎么在别人的屏幕上找到同一格：按标签认（Field 的标签会作为 aria-label 下发到控件上，
 * 表格单元格同理），同标签的按出现顺序数第几个。不用 DOM 下标——两边展开的东西、开的弹窗不一样，下标对不上。
 */
const CONTROLS = "input:not([type=hidden]):not([type=file]), select, textarea";
function controls(): HTMLElement[] {
  return [...document.querySelectorAll<HTMLElement>("[data-collab-root]")].flatMap((r) => [...r.querySelectorAll<HTMLElement>(CONTROLS)]);
}
const labelOf = (el: Element) => (el.getAttribute("aria-label") || el.getAttribute("placeholder") || el.getAttribute("name") || "").slice(0, 40);
export function fieldKeyOf(el: Element | null): CollabPeer["field"] {
  if (!el || !el.matches?.(CONTROLS) || !el.closest("[data-collab-root]")) return null;
  const label = labelOf(el);
  const idx = controls().filter((c) => labelOf(c) === label).indexOf(el as HTMLElement);
  return idx < 0 ? null : { idx, label };
}
export function findField(f: NonNullable<CollabPeer["field"]>): HTMLElement | null {
  return controls().filter((c) => labelOf(c) === f.label)[f.idx] ?? null;
}
